import React, { useState, useEffect } from 'react';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
  List,
  Datagrid,
  TextField,
  Filter,
  TextInput,
  ExportButton,
  DateField,
  ReferenceField,
  DateInput,
  SelectInput,
  useRecordContext,
  useGetOne,
  useListContext,
} from 'react-admin';
import { stringify } from 'qs';
import httpClient from '../utils/httpClient';

const apiUrl = process.env.REACT_APP_BACKEND_URL;

const PAYMENT_STATUSES = [
  { id: 'APPROVED', name: 'APPROVED' },
  { id: 'PENDING', name: 'PENDING' },
  { id: 'FAILED', name: 'FAILED' },
  { id: 'CANCELLED', name: 'CANCELLED' },
];

const PaymentFilter = (props) => (
  <Filter {...props}>
      <TextInput label="Гүйлгээний ID" source="invoiceNo" alwaysOn />
      <DateInput label="Эхлэх огноо" source="startDate" alwaysOn />
      <DateInput label="Дуусах огноо" source="endDate" alwaysOn />
      <SelectInput label="Төлөв" source="status" choices={PAYMENT_STATUSES} alwaysOn />
  </Filter>
);

// Custom field component to render product based on productType
// Uses useGetOne to fetch data directly, avoiding ReferenceField batching issues
const ProductField = ({ label }) => {
  const record = useRecordContext();
  
  if (!record || !record.productId) {
    return <span>-</span>;
  }

  const { productType, productId } = record;

  // Map productType to resource and path
  const getResourceConfig = (type) => {
    switch (type) {
      case 'SUBSCRIPTION':
        return { resource: 'subscriptionPlans', path: '/subscriptionPlans' };
      case 'LINGO_GUIDED_JOURNEY':
        return { resource: 'lingo/journeys', path: '/lingo/journeys' };
      case 'LINGO_LT_QUIZ':
        return { resource: 'lt/quizBundles', path: '/lt/quizBundles' };
      default:
        return null;
    }
  };

  const config = getResourceConfig(productType);

  if (!config) {
    return <span>{productId} (Unknown type: {productType})</span>;
  }

  return <ProductReferenceField productId={productId} config={config} />;
};

// Separate component to use useGetOne hook
const ProductReferenceField = ({ productId, config }) => {
  const { data, isLoading, error } = useGetOne(config.resource, { id: productId });

  if (isLoading) {
    return <span>Loading...</span>;
  }

  if (error || !data) {
    return <span>#{productId} (Not found)</span>;
  }

  return (
    <a href={`#${config.path}/${productId}`} style={{ color: '#1976d2', textDecoration: 'none' }}>
      {data.title || `#${productId}`}
    </a>
  );
};

const PaymentActions = props => {
  const {
    filters,
    showFilter,
    resource,
    displayedFilters,
    filterValues,
    currentSort,
    total,
    exporter,
  } = props;

  return (
    <Toolbar>
      {filters &&
        React.cloneElement(filters, {
          resource,
          showFilter,
          displayedFilters,
          filterValues,
          context: 'button',
        })}
      <ExportButton
        disabled={total === 0}
        resource={resource}
        sort={currentSort}
        filter={filterValues}
        exporter={exporter}
        maxResults={total}
      />
    </Toolbar>
  );
};

const PaymentTotal = () => {
  const { filterValues } = useListContext();
  const [totalAmount, setTotalAmount] = useState(null);
  const filterKey = JSON.stringify(filterValues);

  useEffect(() => {
    const filter = JSON.parse(filterKey);
    httpClient(`${apiUrl}/payments/total?${stringify({ filter })}`)
      .then(({ json }) => setTotalAmount(json.totalAmount))
      .catch(() => setTotalAmount(null));
  }, [filterKey]);

  if (totalAmount === null) return null;

  return (
    <Box sx={{ p: 2, textAlign: 'right' }}>
      <Typography variant="subtitle1">
        Нийт дүн: <strong>{Number(totalAmount).toLocaleString()} ₮</strong>
      </Typography>
    </Box>
  );
};

export const PaymentList = props => (
  <List {...props} filters={<PaymentFilter/>} sort={{ field: 'createdAt', order: 'DESC' }} actions={<PaymentActions props={props} />}>
    <>
      <Datagrid optimized>
        <TextField label="Гүйлгээний ID" source="invoiceNo" />
        <TextField label="Дүн" source="amount" />
        <TextField label="Төлөв" source="status" />
        <TextField label="Алдааны тайлбар" source="errorDesc" />
        <ReferenceField label="Хэрэглэгч"  source="userId" reference="users">
          <TextField source="email" />
        </ReferenceField>
        <ProductField label="Бүтээгдэхүүн" />
        <TextField label="Бүтээгдэхүүны төрөл" source="productType" />
        <TextField label="Provider" source="provider" />
        <DateField showTime label="Үүссэн" source="createdAt" />
        <ReferenceField label="Купон"  source="couponId" reference="coupons">
          <TextField source="code" />
        </ReferenceField>
        <ReferenceField label="Багцын Купон" source="packageCouponId" reference="languageLevelCoupons">
          <TextField source="code" />
        </ReferenceField>
      </Datagrid>
      <PaymentTotal />
    </>
  </List>
);
